/** The service desk sidebar, as data.
 *
 * Three audiences, three sections, and the split is not cosmetic:
 *
 *   EXECUTIVE       — understand the service. Reporting, compliance, posture.
 *   OPERATIONS      — run it. The queues, the catalog, the CMDB, the mail.
 *   ADMINISTRATION  — configure it. Projected from `admin/nav.ts`, so the desk
 *                     sidebar and the administration console cannot disagree
 *                     about what the product contains.
 *
 * Before the navigation audit this was one static array with Administration
 * filed as a single row between Audit and Settings. Thirty-odd configuration
 * screens existed in code and were reachable only by finding that row first and
 * then reading a rail nobody knew was there. The sections are what fixes that;
 * the permissions are what stop it becoming noise.
 *
 * ## Gating
 *
 * Every row names the permissions that reveal it — any one is enough — and
 * `buildAgentNav` is the only thing that decides. Two things follow from that,
 * and both are deliberate:
 *
 * - **No role names, ever.** `is_superuser` and Django groups authorise nothing
 *   in this product. A row gated on "is an admin" would also be invisible to a
 *   custom role, which the permission model has supported since phase 2.
 * - **No separate module check.** `GET /api/agent/me/` returns *effective*
 *   permissions, already narrowed by module entitlement, so an organisation
 *   with the change module switched off does not hold `change.view` and the
 *   Changes and CAB rows simply are not there.
 *
 * This decides what to *render*. It is not enforcement: the server re-checks
 * every request in `core.authz`, and a row that slipped through here would open
 * a screen that shows an error, not data.
 */
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  BarChart3,
  BookOpen,
  Boxes,
  Building2,
  ClipboardList,
  Database,
  FileCheck2,
  FileSearch,
  FileText,
  Gauge,
  GitPullRequestArrow,
  GraduationCap,
  Inbox,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  ListChecks,
  Mail,
  Route,
  ScrollText,
  Send,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  Ticket,
  TrendingUp,
  Workflow,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { NavItem, NavSection } from '../components/layout/nav-model'
import type { Permission } from '../lib/authz'
import { hrefOf, visibleAdminNav, type AdminAccess } from './admin/nav'

/** A desk row before gating. `requires` is the only addition to `NavItem`. */
export interface DeskNavItem {
  to: string
  label: string
  icon: LucideIcon
  end?: boolean
  planned?: boolean
  /** Evaluate `requires` at **platform** scope rather than in the organisation
   *  in scope — for rows whose subject is the platform itself, not a tenant.
   *  Mirrors `AdminItem.platformScoped`; see `holdsFor` in admin/nav.ts. */
  platformScoped?: boolean
  /** Any one of these reveals the row. Empty means every console user sees it —
   *  reaching `/agent` at all already needs `console.access`. */
  requires: Permission[]
  children?: DeskNavItem[]
}

/* -------------------------------------------------------------- executive */

export const EXECUTIVE_NAV: DeskNavItem[] = [
  { to: '/agent/executive', label: 'Overview', icon: TrendingUp, requires: ['report.view'] },
  {
    to: '/agent/reports',
    label: 'Reports & Analytics',
    icon: BarChart3,
    requires: ['report.view'],
  },
  {
    to: '/agent/compliance',
    label: 'Compliance',
    icon: ScrollText,
    // The parent is a disclosure, not a link. It shows when any child does,
    // which `buildAgentNav` works out rather than this list asserting it.
    requires: ['compliance.view', 'risk.view', 'audit.view'],
    children: [
      { to: '/agent/compliance/soc2', label: 'SOC 2', icon: ShieldCheck, requires: ['compliance.view'] },
      {
        to: '/agent/compliance/iso27001',
        label: 'ISO 27001',
        icon: ShieldCheck,
        requires: ['compliance.view'],
      },
      { to: '/agent/compliance/cmmi', label: 'CMMI L3', icon: Layers, requires: ['compliance.view'] },
      {
        to: '/agent/compliance/risks',
        label: 'Risk Register',
        icon: AlertTriangle,
        requires: ['risk.view'],
      },
      {
        to: '/agent/compliance/controls',
        label: 'Controls',
        icon: ListChecks,
        requires: ['compliance.view'],
      },
      {
        to: '/agent/compliance/evidence',
        label: 'Evidence',
        icon: FileCheck2,
        requires: ['compliance.view'],
      },
      {
        to: '/agent/compliance/readiness',
        label: 'Audit Readiness',
        icon: Gauge,
        requires: ['compliance.view'],
      },
      { to: '/agent/compliance/audit', label: 'Audit Trail', icon: FileSearch, requires: ['audit.view'] },
      // Specified in the Compliance Center brief, with no backing model in the
      // platform. Marked planned rather than omitted so the intended shape is
      // visible and the gap is explicit — each is ranked in
      // docs/COMPLIANCE-BACKLOG.md. A screen with invented policies or vendors
      // would be worse than an honest "not built".
      {
        to: '/agent/compliance/policies',
        label: 'Policies',
        icon: ScrollText,
        planned: true,
        requires: ['compliance.view'],
      },
      {
        to: '/agent/compliance/vendors',
        label: 'Vendors',
        icon: Building2,
        planned: true,
        requires: ['compliance.view'],
      },
      {
        to: '/agent/compliance/continuity',
        label: 'Continuity & DR',
        icon: LifeBuoy,
        planned: true,
        requires: ['compliance.view'],
      },
      {
        to: '/agent/compliance/awareness',
        label: 'Security Awareness',
        icon: GraduationCap,
        planned: true,
        requires: ['compliance.view'],
      },
    ],
  },
  {
    to: '/agent/bi',
    label: 'Business Intelligence',
    icon: Database,
    planned: true,
    requires: ['report.export'],
  },
  {
    // Endpoint security, kept out of the Compliance group on purpose. Audit
    // Trail sits in there and answers "who changed this system"; these two
    // answer "what did the estate do", from a different table, on a different
    // permission, for a different reader. Filing them together is how an
    // operator ends up looking for a device event in the administrative trail.
    to: '/agent/security/events',
    label: 'Security Events',
    icon: ShieldAlert,
    requires: ['security.view'],
    children: [
      {
        to: '/agent/security/events',
        label: 'Event Log',
        icon: ScrollText,
        requires: ['security.view'],
      },
      {
        to: '/agent/security/alerts',
        label: 'Findings',
        icon: ShieldAlert,
        requires: ['security.view'],
      },
    ],
  },
  {
    // Was `planned: true` — a false claim since the screen and
    // `GET /api/agent/system/health/` shipped. It also needs `platformScoped`,
    // for the reason the administration rail already carries it: the endpoint
    // refuses `settings.view` held only in an organisation, so an Organization
    // Admin gated on the tenant grant alone would be shown a row that 403s.
    to: '/agent/system-health',
    label: 'System Health',
    icon: Activity,
    platformScoped: true,
    requires: ['settings.view'],
  },
]

/* ------------------------------------------------------------- operations */

export const OPERATIONS_NAV: DeskNavItem[] = [
  { to: '/agent', label: 'Service Desk', icon: LayoutDashboard, end: true, requires: [] },
  { to: '/agent/tickets', label: 'Tickets', icon: Ticket, requires: ['ticket.view_all'] },
  { to: '/agent/incidents', label: 'Incidents', icon: AlertCircle, requires: ['ticket.view_all'] },
  {
    to: '/agent/requests',
    label: 'Service Requests',
    icon: ClipboardList,
    requires: ['ticket.view_all'],
  },
  { to: '/agent/problems', label: 'Problems', icon: LifeBuoy, requires: ['problem.view'] },
  {
    to: '/agent/changes',
    label: 'Changes',
    icon: GitPullRequestArrow,
    requires: ['change.view'],
  },
  { to: '/agent/cab', label: 'CAB', icon: ShieldCheck, requires: ['change.view'] },
  { to: '/agent/catalog', label: 'Service Catalog', icon: LayoutGrid, requires: ['catalog.view'] },
  { to: '/agent/assets', label: 'Assets', icon: Boxes, requires: ['asset.view'] },
  {
    to: '/agent/kb',
    label: 'Knowledge Base',
    icon: BookOpen,
    requires: ['knowledge.view_internal'],
  },
  { to: '/agent/automation', label: 'Automation', icon: Workflow, requires: ['workflow.view'] },
  {
    to: '/agent/email',
    label: 'Email',
    icon: Mail,
    requires: ['email.view'],
    children: [
      { to: '/agent/email/outbound', label: 'Outgoing', icon: Send, requires: ['email.view'] },
      { to: '/agent/email/inbound', label: 'Incoming', icon: Inbox, requires: ['email.view'] },
      { to: '/agent/email/routes', label: 'Routing', icon: Route, requires: ['email.view'] },
      { to: '/agent/email/templates', label: 'Templates', icon: FileText, requires: ['email.view'] },
    ],
  },
  { to: '/agent/audit', label: 'Audit', icon: ScrollText, requires: ['audit.view'] },
  // Personal preferences, not configuration — which is why this stays in
  // Operations rather than moving into ADMINISTRATION with the settings tree.
  { to: '/agent/settings', label: 'Settings', icon: Settings2, planned: true, requires: [] },
]

/* ----------------------------------------------------------------- build */

/** Whether the caller holds what a row asks for, at the scope it asks at.
 *
 *  The same rule as `holdsFor` in admin/nav.ts, and deliberately duplicated
 *  rather than imported: that one takes an `AdminItem`, this takes a
 *  `DeskNavItem`, and the two lists are separate by design. Four lines is a
 *  cheaper coupling than a shared supertype.
 */
function holds(access: AdminAccess, item: DeskNavItem): boolean {
  if (item.platformScoped) {
    return item.requires.some((permission) => access.canOnPlatform?.(permission) ?? false)
  }
  return item.requires.some(access.can)
}

function gate(access: AdminAccess, item: DeskNavItem): NavItem | null {
  const children = (item.children ?? [])
    .map((child) => gate(access, child))
    .filter((child): child is NavItem => child !== null)

  // A disclosure with nothing under it is worse than no row: it opens onto
  // nothing. So a parent survives on its children, not on its own permissions.
  if (item.children && children.length === 0) return null
  if (item.requires.length > 0 && !holds(access, item)) return null

  const { requires: _requires, children: _children, ...rest } = item
  return children.length > 0 ? { ...rest, children } : rest
}

function section(heading: string, items: DeskNavItem[], access: AdminAccess): NavSection[] {
  const visible = items.map((item) => gate(access, item)).filter((item): item is NavItem => item !== null)
  return visible.length > 0 ? [{ heading, items: visible }] : []
}

/** ADMINISTRATION, projected from the administration console's own rail.
 *
 *  Each of the console's eight groups becomes one collapsible row here, so the
 *  desk sidebar gains thirty-odd reachable destinations and eight lines of
 *  height. Rows the caller cannot use are already gone: `visibleAdminNav` has
 *  filtered them, and a group that empties out does not appear.
 */
function administration(access: AdminAccess): NavSection[] {
  const items = visibleAdminNav(access)
    .map((group) => {
      const children = group.items
        .filter((item) => !item.deskHidden)
        .map((item) => ({
          to: hrefOf(item),
          label: item.label,
          icon: item.icon,
          // "soon" in the sidebar is the same claim the destination makes at
          // full length. Both read `backed`, so they cannot disagree.
          planned: item.backed !== true,
        }))
      return { to: `#${group.heading}`, label: group.heading, icon: group.icon, children }
    })
    .filter((group) => group.children.length > 0)

  return items.length > 0 ? [{ heading: 'Administration', items }] : []
}

/** The whole sidebar, for one caller. */
export function buildAgentNav(access: AdminAccess): NavSection[] {
  return [
    ...section('Executive', EXECUTIVE_NAV, access),
    ...section('Operations', OPERATIONS_NAV, access),
    ...administration(access),
  ]
}

/** Every destination the sidebar can reach, flattened. Used by the audit test
 *  that checks each one against the route table. */
export function navDestinations(sections: NavSection[]): string[] {
  const out: string[] = []
  const walk = (items: NavItem[]) => {
    for (const item of items) {
      // Group parents address themselves with a fragment; they are disclosures,
      // not destinations, and there is no route for them to match.
      if (!item.to.startsWith('#')) out.push(item.to)
      if (item.children) walk(item.children)
    }
  }
  for (const s of sections) walk(s.items)
  return out
}
